import {
  OPPORTUNITIES_FETCH_REQUEST,
  OPPORTUNITIES_FETCH_SUCCESS,
  OPPORTUNITIES_FETCH_FAILURE,
  OPPORTUNITIES_CREATE_REQUEST,
  OPPORTUNITIES_CREATE_SUCCESS,
  OPPORTUNITIES_CREATE_FAILURE,
  OPPORTUNITIES_UPDATE_REQUEST,
  OPPORTUNITIES_UPDATE_SUCCESS,
  OPPORTUNITIES_UPDATE_FAILURE,
  OPPORTUNITIES_DELETE_REQUEST,
  OPPORTUNITIES_DELETE_SUCCESS,
  OPPORTUNITIES_DELETE_FAILURE,
  OPPORTUNITIES_MOVE_REQUEST,
  OPPORTUNITIES_MOVE_SUCCESS,
  OPPORTUNITIES_MOVE_FAILURE,
  OPPORTUNITIES_UPSERT_ONE,
  OPPORTUNITIES_REMOVE_ONE,
  OPPORTUNITIES_CLIENT_FEEDBACK_LOGGED,
  SET_ENVIRONMENT,
} from '../actions/actionTypes';

interface OpportunitiesState {
  opportunities: any[];
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;
  /**
   * `${opportunityId}:${profileId}` for every client feedback entry stored from this tab (ENG-340).
   * Entries are append-only, so these stay logged: a list or card read that started before the save
   * and lands after it can't bring the "Client feedback missing" strip back.
   */
  clientFeedbackLogged: string[];
}

const initialState: OpportunitiesState = {
  opportunities: [],
  loading: false,
  error: null,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  clientFeedbackLogged: [],
};

const feedbackKey = (opportunityId: string, profileId: string) => `${opportunityId}:${profileId}`;

/** The opportunity with clientFeedbackLogged set on each talent logged from this tab. */
function withLoggedFeedback(opp: any, logged: string[]): any {
  if (!logged.length || !Array.isArray(opp?.talents)) return opp;
  let changed = false;
  const talents = opp.talents.map((t: any) => {
    if (t.clientFeedbackLogged === true || !t.talentProfileId || !logged.includes(feedbackKey(opp.id, t.talentProfileId))) return t;
    changed = true;
    return { ...t, clientFeedbackLogged: true };
  });
  return changed ? { ...opp, talents } : opp;
}

export default function opportunitiesReducer(
  state = initialState,
  action: any,
): OpportunitiesState {
  switch (action.type) {
    case OPPORTUNITIES_FETCH_REQUEST:
      return { ...state, loading: true, error: null };
    case OPPORTUNITIES_FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        opportunities: action.payload.map((o: any) => withLoggedFeedback(o, state.clientFeedbackLogged)),
      };
    case OPPORTUNITIES_FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload };

    case OPPORTUNITIES_CREATE_REQUEST:
      return { ...state, createLoading: true };
    case OPPORTUNITIES_CREATE_SUCCESS:
      return { ...state, createLoading: false };
    case OPPORTUNITIES_CREATE_FAILURE:
      return { ...state, createLoading: false, error: action.payload };

    case OPPORTUNITIES_UPDATE_REQUEST:
      return { ...state, updateLoading: true };
    case OPPORTUNITIES_UPDATE_SUCCESS:
      return { ...state, updateLoading: false };
    case OPPORTUNITIES_UPDATE_FAILURE:
      return { ...state, updateLoading: false, error: action.payload };

    case OPPORTUNITIES_DELETE_REQUEST:
      return { ...state, deleteLoading: true };
    case OPPORTUNITIES_DELETE_SUCCESS:
      return { ...state, deleteLoading: false };
    case OPPORTUNITIES_DELETE_FAILURE:
      return { ...state, deleteLoading: false, error: action.payload };

    case OPPORTUNITIES_MOVE_REQUEST:
      // Optimistic update: move locally before server confirms
      if (action.payload) {
        const { id, statusInternal } = action.payload;
        return {
          ...state,
          updateLoading: true,
          opportunities: state.opportunities.map((o) =>
            o.id === id ? { ...o, statusInternal } : o
          ),
        };
      }
      return { ...state, updateLoading: true };
    case OPPORTUNITIES_MOVE_SUCCESS:
      return { ...state, updateLoading: false };
    case OPPORTUNITIES_MOVE_FAILURE:
      return { ...state, updateLoading: false, error: action.payload };

    case OPPORTUNITIES_UPSERT_ONE: {
      const updated = withLoggedFeedback(action.payload, state.clientFeedbackLogged);
      const idx = state.opportunities.findIndex((o) => o.id === updated.id);
      const list = [...state.opportunities];
      if (idx >= 0) {
        list[idx] = updated;
      } else {
        list.unshift(updated);
      }
      return { ...state, opportunities: list };
    }

    case OPPORTUNITIES_CLIENT_FEEDBACK_LOGGED: {
      const key = feedbackKey(action.payload.opportunityId, action.payload.profileId);
      const clientFeedbackLogged = state.clientFeedbackLogged.includes(key)
        ? state.clientFeedbackLogged
        : [...state.clientFeedbackLogged, key];
      return {
        ...state,
        clientFeedbackLogged,
        opportunities: state.opportunities.map((o) => withLoggedFeedback(o, clientFeedbackLogged)),
      };
    }

    // The marks are one environment's ids: another environment's rows never take them.
    case SET_ENVIRONMENT:
      return state.clientFeedbackLogged.length ? { ...state, clientFeedbackLogged: [] } : state;

    case OPPORTUNITIES_REMOVE_ONE:
      return {
        ...state,
        opportunities: state.opportunities.filter((o) => o.id !== action.payload),
      };

    default:
      return state;
  }
}
